// LocalServer — a browser-runnable Munos server instance that hosts the
// SERVER halves of a launched set, so a player's own machine plays both client
// and server (docs/multi-script-client.md §"The lobby's role"; the Quake "even
// solo, you're a local client of a local server" model). It hosts an ARRAY of
// member scripts on one shared local bus — the same multi-member shape the real
// server's ScriptInstance runs (packages/server/src/instance.ts), minus the
// Node transport.
//
// It is multi-client: connectSlot/feedFrom/disconnectSlot are keyed by slot and
// the interest table spans maxClients. Singleplayer uses only slot 0 (the
// loopback). In P2P host mode (docs/p2p.md) the SAME server also serves remote
// peers on slots 1+ — the host's browser is the server, peers connect over
// WebRTC, and connectPeer hands out their slots. LocalServer stays
// transport-agnostic: it emits per-slot through its sink, and the owner routes
// each slot to the loopback or a peer's data channels.
//
// How it mirrors ScriptInstance:
//   - a MEMBER ARRAY: each member is its own wasm instance with its own
//     memory / heap / async-op queue / `listen` channel set / persistence
//     scope; the slot table, the interest geometry, and the tick counter
//     are SHARED across members (one client connection = one slot every
//     member sees);
//   - per-member `asyncOps.drain()` after every event — the step
//     exactly what makes `db_*` callbacks fire;
//   - REAL persistence: each member's `db_*` binds through makeDbEnv to a
//     browser-as-server KV backend (OPFS-SQLite, its own `.munos-server`
//     directory at the 10 MB server quota), with an in-memory fallback when
//     the browser can't run the OPFS VFS.
//
// Transport is loopback: LoopbackBusSocket (loopback-bus-socket.ts) bridges
// the client-side BusConnection to this server through `feedFrom` (client →
// server) and the `setSink` callback (server → client), so the riders on the
// bus never know they're talking to a server in the same tab.

import { defaultCompiler } from "./compiler-default.js";
import {
  type MunosCompiler,
  CompileError,
  type ConstValue,
  constValuesEqual,
  describeConstDifference,
  makeBaseEnv,
  makeDbEnv,
  AsyncOpQueue,
  encodeBytes,
  readBytesPtr,
  readStringPtr,
  allocString,
  InterestTable,
  encodeEnvelope,
  decodeEnvelope,
  type MunosKvBackend,
} from "@multinostalgia/munos";
import { getSharedServerKv } from "./persistent-server-db.js";
import type { BusSocket, BusSocketFactory } from "./bus-connection.js";

// Shared-bus channel framing: every frame leads with a little-endian u16
// channel id. The host stamps it outbound and strips + routes it inbound, so
// the wasm payload is the bare body (docs/multi-script-client.md).
function stampChannel(channel: number, body: Uint8Array): Uint8Array {
  const out = new Uint8Array(body.length + 2);
  out[0] = channel & 0xff;
  out[1] = (channel >> 8) & 0xff;
  out.set(body, 2);
  return out;
}
function readChannel(frame: Uint8Array): number {
  return frame.length >= 2 ? (frame[0]! | (frame[1]! << 8)) : -1;
}

// Server→client delivery. `toSlot` is which connected slot the frame is for.
// In the singleplayer loopback there is exactly one (slot 0 = the local
// player). When this server also hosts remote peers (P2P host mode, slots 1+),
// the owner routes each `toSlot` to the right peer transport. `reliable` picks
// the peer's channel — the unreliable class takes WebRTC's unordered,
// zero-retransmit channel; over the loopback it's ignored (there is no wire).
export type LocalServerSend = (toSlot: number, bytes: Uint8Array, reliable: boolean) => void;
// Interest-management leave signal — the server tells a receiver a subject
// left its interest set. The loopback socket turns this into the client's
// `event peer_left(slot)`.
export type LocalServerPeerLeft = (toSlot: number, slot: number) => void;

export interface LocalServerOptions {
  maxClients?: number;
  tickHz?: number;
  // Persistence backend override. Production omits it (the OPFS-SQLite store is
  // created, with an in-memory fallback); headless tests inject an
  // InMemoryMunosKv so they never touch a Worker/OPFS.
  kv?: MunosKvBackend;
  // When true, do NOT start the setInterval tick timer — the owner drives
  // ticks via tick(). Used by deterministic tests; production leaves it false
  // so the server ticks on its own clock.
  manualTick?: boolean;
  // Factory for the parent-uplink socket (docs/node-federation.md). Production
  // omits it — a real WebSocket to the origin. Tests inject a fake BusSocket to
  // exercise the connect_up / send_up / receive_from_above protocol with no
  // real server.
  uplinkFactory?: BusSocketFactory;
  // Compiler backend. Production/tests omit it and get the in-process
  // TypeScript compiler; the Rust/WASM backend is injected here once proven
  // (docs/rust-compiler-migration.md).
  compiler?: MunosCompiler;
}

// Unguessable, ASCII-safe key fragment for KV keys (e.g. a recorder run id) —
// the browser counterpart to the server's `uuid()` (instance.ts uses
// randomUUID with dashes stripped). Falls back to a random hex string where
// crypto.randomUUID is unavailable.
function makeUuid(): string {
  const c: Crypto | undefined = (globalThis as { crypto?: Crypto }).crypto;
  if (c && typeof c.randomUUID === "function") return c.randomUUID().replace(/-/g, "");
  let s = "";
  for (let i = 0; i < 32; i++) s += Math.floor(Math.random() * 16).toString(16);
  return s;
}

// One hosted server-half member. Owns its own wasm instance + heap, so it
// satisfies the structural HeapHost interface (memory/view/heapPtrGlobal are
// public for that reason). Members share nothing but the instance's
// networking fabric.
class Member {
  memory!: WebAssembly.Memory;
  view!: DataView;
  heapPtrGlobal!: WebAssembly.Global;
  instance!: WebAssembly.Instance;
  // Channels this member `listen`s on (filled by register_channel after init).
  readonly channels = new Set<number>();
  // True when this member's `event receive` declared the optional leading
  // `channel: i32` param; deliverFrame then passes the routed channel to it.
  receiveWantsChannel = false;
  readonly asyncOps: AsyncOpQueue;

  constructor(readonly label: string, readonly scope: string, readonly sourceText: string = "") {
    // getExports is a thunk: `instance` is assigned during instantiate, before
    // any event fires, so the queue resolves it lazily and correctly.
    this.asyncOps = new AsyncOpQueue(this, () => this.instance.exports, {
      label: `local-server:${label}`,
      role: "server",
    });
  }

  refreshView(): void {
    this.view = new DataView(this.memory.buffer);
  }
}

export class LocalServer {
  private readonly members: Member[] = [];
  // Accumulated `shared const` contract across added members (docs/language.md
  // §"Shared constants"): name → the first member that declared it. A member
  // whose value for a name disagrees with the first is a drifted wire contract
  // and is refused (the browser analog of the server's refuse-to-start).
  private readonly sharedConstContract = new Map<string, { label: string; value: ConstValue }>();
  private readonly interest: InterestTable;
  // Slots that have fired event_server_connect (shared across members).
  private readonly connectedSlots = new Set<number>();
  private tickHz: number;
  private tickNum = 0;
  private tickTimer: ReturnType<typeof setInterval> | null = null;
  private stopped = false;
  private sink: LocalServerSend | null = null;
  private peerLeftSink: LocalServerPeerLeft | null = null;
  private readonly manualTick: boolean;
  // Per-recipient outbound buffers, split by reliability class. A tick's relays
  // (and any send-on-receive) accumulate here per destination slot and
  // flushEgress() ships each slot's frames as coalesced envelopes. Keyed by slot
  // because interest culling makes each recipient's frame set distinct
  // (docs/coalescing.md). The two classes are buffered and flushed separately so
  // a reliable frame and an unreliable one never share a datagram — the loopback
  // delivers both the same way, but a remote peer takes the unreliable class on
  // WebRTC's unordered, zero-retransmit channel (mirrors ScriptInstance).
  private readonly outbound = new Map<number, { reliable: Uint8Array[]; unreliable: Uint8Array[] }>();

  // Parent uplink (docs/node-federation.md). connect_up opens an OUTBOUND
  // socket to the origin (the central server this browser-local node reaches
  // up to). Over it this node is an ordinary client — handoff first, then
  // channel-stamped envelopes — so the origin needs no special case. send_up
  // writes here; frames arriving back fire event_server_receive_from_above.
  // Datagrams sent before the socket opens queue here.
  private parentLink: BusSocket | null = null;
  private parentLinkReady = false;
  private readonly parentLinkQueue: Uint8Array[] = [];
  // The origin this node was rooted under (adoptParent / connect_up), or "" if
  // it is its own root. Set the moment the uplink is established — before the
  // socket finishes opening — since "we have a parent" is an intent, not a
  // connection state. parent_url() returns it so a hosted script can tell
  // whether an upward path exists (e.g. the editor hides its cloud-publish
  // action when it's a local-only root). Cleared only on full teardown.
  private parentUrl = "";

  private constructor(
    private readonly kv: MunosKvBackend,
    private readonly maxClients: number,
    tickHz: number,
    manualTick: boolean,
    private readonly uplinkFactory: BusSocketFactory,
    private readonly compiler: MunosCompiler,
  ) {
    this.tickHz = tickHz;
    this.manualTick = manualTick;
    this.interest = new InterestTable(maxClients);
  }

  // Stand up the server. Async only because the persistent backend spins a
  // worker + provisions its schema on first use. The KV is the page-lived
  // shared singleton (persistent-server-db.ts) — one connection for every
  // tile's server AND the admin panel — so this server NEVER owns or closes
  // it. Tests pass `kv` to inject an in-memory store and skip OPFS entirely.
  static async create(opts: LocalServerOptions = {}): Promise<LocalServer> {
    const maxClients = opts.maxClients ?? 64;
    const tickHz = opts.tickHz ?? 60;
    const manualTick = opts.manualTick ?? false;
    const kv = opts.kv ?? (await getSharedServerKv());
    const uplinkFactory: BusSocketFactory =
      opts.uplinkFactory ?? ((u) => new WebSocket(u));
    const compiler = opts.compiler ?? defaultCompiler();
    if (compiler === undefined) {
      throw new Error(
        "LocalServer requires a compiler backend — production injects the WASM " +
          "compiler (main.ts/local-hub.ts); tests register one via test-setup.ts.",
      );
    }
    return new LocalServer(kv, maxClients, tickHz, manualTick, uplinkFactory, compiler);
  }

  // Drive `times` server ticks synchronously (deterministic tests). Production
  // ticks on the setInterval timer instead; this is only meaningful when the
  // server was created with manualTick.
  tick(times = 1): void {
    for (let i = 0; i < times; i++) this.fireTick();
  }

  // Wire the loopback transport: `send` carries a server→client frame, and
  // `peerLeft` carries an interest-management leave. Called once by the
  // LoopbackBusSocket that adopts this server.
  setSink(send: LocalServerSend, peerLeft?: LocalServerPeerLeft): void {
    this.sink = send;
    this.peerLeftSink = peerLeft ?? null;
  }

  // Host-established parent uplink (docs/node-federation.md, Stage C). When the
  // browser host acts on a `host_locally` instruction, it spawns THIS local
  // server AND wires its uplink to the parent — one inherent step, since the
  // parent (who sent the instruction) is a host-level fact, not game logic. So
  // the editor's server{} just `send_up`s and never names an address. This is
  // the counterpart of the script-side `connect_up` (which a genuinely spawned
  // node calls itself); both establish the one parent link, and `handoff` is
  // the connect handshake the parent reads in its `event connect`.
  adoptParent(url: string, handoff: Uint8Array = new Uint8Array()): void {
    this.openParentLink(url, handoff);
  }

  get memberCount(): number {
    return this.members.length;
  }

  // Whether any hosted member has real server-side networking — i.e. `listen`s
  // on at least one channel, so a joining client would actually interact with
  // it. A cosmetic / client-only mod (e.g. "no music") has an inert server half
  // that registers no channels, so this stays false: there's nothing to host,
  // and the Share UI keys off it rather than mere member presence.
  get hasNetworkedMember(): boolean {
    return this.members.some((m) => m.channels.size > 0);
  }

  // Compile, instantiate, and init one member's SERVER half. The host calls
  // this for each launched mod (a mod whose server block is empty becomes an
  // inert member — harmless). Idempotent per label. A member added AFTER a
  // slot connected replays that slot's connect, so a late-joining recorder
  // still sees the already-present player.
  // `initData` is the per-instance spawn seed delivered to the member's
  // `event init` — the browser-local mirror of the real server's
  // ScriptInstance.init (instance.ts), which fires init with spawn(...)'s
  // payload. A local deployment mount passes the chosen level's blob so a
  // hosted store is born holding the level (docs/level-player-sessions.md §4);
  // a couch / host-locally mount omits it (an empty seed, as before).
  addMember(source: string, label: string, scope: string, initData: Uint8Array = new Uint8Array(0)): void {
    if (this.stopped) return;
    // An empty label is a bug upstream (the source-fetch JSON envelope should
    // carry each member's label). It must NOT silently collapse the set: the
    // dedup below is by label, so every empty-labelled member would look like a
    // duplicate of the first and only ONE server half would ever load — and
    // which one is a spawn-order race. Warn loudly and skip the dedup so all
    // distinct members still come up.
    if (label === "") {
      console.error(
        "[local-server] addMember got an EMPTY label — labels are the per-member dedup key + persistence scope; check the source-fetch JSON envelope (scripts[].label). Deduping by SOURCE instead so a re-host doesn't compound duplicates, while distinct empty-labelled sources still both load.",
      );
      // Can't dedup by label (every empty label would collapse to one member —
      // a spawn-order race), so fall back to SOURCE as the key: a repeated
      // reconcile / host-locally re-adding the same source is idempotent, but
      // two DISTINCT empty-labelled sources still both come up.
      if (this.members.some((m) => m.label === "" && m.sourceText === source)) return;
    } else if (this.members.some((m) => m.label === label)) {
      return;
    }
    let compiled;
    try {
      compiled = this.compiler.compile(source, "server");
    } catch (e) {
      if (e instanceof CompileError) {
        console.error(`[local-server] compile failed for '${label}': ${e.message}`);
        return;
      }
      throw e;
    }
    // Shared-const agreement (docs/language.md §"Shared constants"): a member
    // whose value for a name disagrees with an already-mounted member is a
    // drifted wire contract. Refuse it (loudly) rather than run an incompatible
    // half — the browser has no whole-bundle "refuse to start", so refusing the
    // disagreeing member plus a prominent error is the faithful analog.
    for (const sc of compiled.sharedConsts) {
      const ref = this.sharedConstContract.get(sc.name);
      if (ref !== undefined && !constValuesEqual(ref.value, sc.value)) {
        console.error(
          `[local-server] shared const disagreement on '${sc.name}': ` +
          `'${ref.label}' and '${label}' declare different values ` +
          `(${describeConstDifference(ref.value, sc.value)}). ` +
          `Not loading '${label}' — co-hosted members must agree on every shared const.`,
        );
        return;
      }
    }
    const m = new Member(label, scope, source);
    this.instantiate(m, compiled.wasm);
    // init() traps propagate raw — a broken member aborts its own add, not the
    // whole server.
    const initFn = m.instance.exports.init as (() => void) | undefined;
    if (initFn) initFn();
    // Collect this member's server-half `listen` subscriptions (after init, so
    // a `const` channel global is set).
    const reg = m.instance.exports.__register_channels_server as (() => void) | undefined;
    if (reg) reg();
    m.receiveWantsChannel = !!m.instance.exports.__receive_wants_channel_server;
    void m.asyncOps.drain();
    // event init fires once at birth with the per-instance seed — empty for a
    // couch / host-locally mount (like the entry instance on the real server),
    // or the spawn payload (e.g. a Level Player level blob) for a seeded local
    // deployment mount. Mirrors ScriptInstance.init (instance.ts).
    this.callEvent(m, "event_server_init", encodeBytes(m, initData));
    this.members.push(m);
    // Record this member's shared consts now that it is live, so the next
    // member is checked against it (a member whose init trapped above never
    // reaches here, so it never pollutes the contract).
    for (const sc of compiled.sharedConsts) {
      if (!this.sharedConstContract.has(sc.name)) {
        this.sharedConstContract.set(sc.name, { label, value: sc.value });
      }
    }
    // Replay connect for any slot that connected before this member existed.
    for (const slot of this.connectedSlots) {
      this.callEvent(m, "event_server_connect", slot, encodeBytes(m, new Uint8Array(0)));
    }
    if (!this.manualTick && this.tickTimer === null) this.startTickTimer();
  }

  // Fire event_server_connect across all members for a caller-chosen slot. The
  // loopback has a single player, so it passes slot 0; a remote peer's slot is
  // handed out by connectPeer (below). No-op if the slot is already connected.
  connectSlot(slot: number, handoff: Uint8Array): void {
    if (this.stopped || this.connectedSlots.has(slot)) return;
    this.connectedSlots.add(slot);
    // Reset this slot's interest region + relay edges before connect runs.
    this.interest.clearSlot(slot);
    for (const m of this.members) {
      this.callEvent(m, "event_server_connect", slot, encodeBytes(m, handoff));
    }
  }

  // Reserve the lowest free slot WITHOUT connecting it. Returns -1 when the
  // server is full or stopped. A router that must register a slot's client
  // before the connect fires (so a member's connect-time send already routes)
  // calls this, records the mapping, then connectSlot — see PeerHost.attachLocal.
  // Both calls run synchronously with no await between them, so no two clients
  // race onto the same slot.
  allocSlot(): number {
    if (this.stopped) return -1;
    for (let s = 0; s < this.maxClients; s++) {
      if (!this.connectedSlots.has(s)) return s;
    }
    return -1;
  }

  // Connect a freshly-arrived REMOTE peer (P2P host mode): pick the lowest free
  // slot, fire connect across members with the peer's handoff, and return the
  // slot so the owner can map it to that peer's transport. Returns -1 when the
  // server is full or stopped. Slot 0 is normally the host's own loopback
  // client, so peers land on 1+; alloc + connect run in one synchronous step,
  // so two peers whose handoffs arrive back-to-back never race onto one slot.
  connectPeer(handoff: Uint8Array): number {
    const slot = this.allocSlot();
    if (slot < 0) return -1;
    this.connectSlot(slot, handoff);
    return slot;
  }

  // The loopback socket closed: fire disconnect across members and emit
  // peer_left for everyone who was drawing this slot, mirroring the server's
  // ws.on("close") path.
  disconnectSlot(slot: number): void {
    if (!this.connectedSlots.delete(slot)) return;
    for (const m of this.members) {
      this.callEvent(m, "event_server_disconnect", slot);
    }
    for (const r of this.interest.subjectGone(slot, (s) => this.connectedSlots.has(s))) {
      if (this.peerLeftSink) this.peerLeftSink(r, slot);
    }
    this.interest.clearSlot(slot);
  }

  // Deliver a client-originated frame as if it arrived on the wire from
  // `slot`. Channel-demuxed: the bare body reaches only members that `listen`
  // on the frame's channel.
  feedFrom(slot: number, datagram: Uint8Array): void {
    if (this.stopped || !this.connectedSlots.has(slot)) return;
    // The client ships one datagram carrying several frames; split it back.
    for (const f of decodeEnvelope(datagram)) this.deliverFrame(slot, f);
    // A member may have relayed/sent while handling the receive; ship it.
    this.flushEgress();
  }

  // Deliver one bare-framed message to the members that listen on its channel.
  private deliverFrame(slot: number, frame: Uint8Array): void {
    const channel = readChannel(frame);
    if (channel < 0) return;
    const body = frame.subarray(2);
    for (const m of this.members) {
      if (!m.channels.has(channel)) continue;
      if (m.receiveWantsChannel) {
        this.callEvent(m, "event_server_receive", slot, channel, encodeBytes(m, body));
      } else {
        this.callEvent(m, "event_server_receive", slot, encodeBytes(m, body));
      }
    }
  }

  // Egress: buffer a stamped frame under its recipient slot and reliability
  // class for the next flushEgress().
  private bufferOutbound(to: number, data: Uint8Array, reliable: boolean): void {
    let buf = this.outbound.get(to);
    if (!buf) { buf = { reliable: [], unreliable: [] }; this.outbound.set(to, buf); }
    (reliable ? buf.reliable : buf.unreliable).push(data);
  }

  // Ship every recipient's buffered frames as coalesced envelopes (one flush
  // per tick and after each inbound fan-out). Each reliability class coalesces
  // into its own datagram(s) and carries its class to the sink, so an unreliable
  // frame never rides a reliable datagram (or vice versa). No-op unless
  // coalescing.
  private flushEgress(): void {
    if (this.outbound.size === 0) return;
    if (this.sink) {
      for (const [to, buf] of this.outbound) {
        for (const datagram of encodeEnvelope(buf.reliable)) this.sink(to, datagram, true);
        for (const datagram of encodeEnvelope(buf.unreliable)) this.sink(to, datagram, false);
      }
    }
    this.outbound.clear();
  }

  // Tear down: stop ticking and abandon pending callbacks. The KV backend is
  // the shared page-lived singleton (or a test-injected store), so it is NOT
  // closed here — a later singleplayer launch reuses the same connection, and
  // the admin panel may still be reading it.
  stop(): void {
    if (this.stopped) return;
    this.stopped = true;
    if (this.tickTimer !== null) {
      clearInterval(this.tickTimer);
      this.tickTimer = null;
    }
    if (this.parentLink) {
      try { this.parentLink.close(); } catch { /* already closing */ }
      this.parentLink = null;
      this.parentLinkReady = false;
    }
    this.parentUrl = "";
    for (const m of this.members) m.asyncOps.clear();
  }

  // ----- instantiation -----

  private instantiate(m: Member, wasm: Uint8Array): void {
    const env = this.buildMemberEnv(m);
    const mod = new WebAssembly.Module(wasm as BufferSource);
    m.instance = new WebAssembly.Instance(mod, { env });
    m.memory = m.instance.exports.memory as WebAssembly.Memory;
    m.heapPtrGlobal = m.instance.exports.heap_ptr as WebAssembly.Global;
    m.view = new DataView(m.memory.buffer);
  }

  // Invoke a member's optional exported event handler, then drain its async-op
  // queue so any db callbacks the handler issued fire before the next event.
  // Returns false only when the handler threw.
  private callEvent(m: Member, name: string, ...args: number[]): boolean {
    const fn = m.instance.exports[name] as ((...a: number[]) => void) | undefined;
    let ok = true;
    if (fn) {
      try {
        fn(...args);
      } catch (e) {
        console.error(`[local-server] ${m.label}.${name} threw:`, e);
        ok = false;
      }
    }
    // Fire-and-forget drain (matches ScriptInstance.callEvent): a slow backend
    // never stalls the dispatch loop; the completion lands on a later microtask.
    void m.asyncOps.drain();
    return ok;
  }

  // ----- env imports (called from a member's wasm) -----

  private buildMemberEnv(m: Member): WebAssembly.Imports["env"] {
    return {
      // Base scaffold: emulator/image/input/ROM/hop/instance stubs with safe
      // returns (a server member touches none of them). Persistence + the
      // networking + clock imports below override the base stubs.
      ...makeBaseEnv(m, { label: `local-server:${m.label}` }),
      // Real persistence, scoped to this member's label (server-side scope =
      // label, docs/persistence.md). `@`-namespaces route to the shared tier.
      ...makeDbEnv(m, m.asyncOps, this.kv, m.scope),

      // Networking. send_client has no server-side meaning (the checker rejects
      // it from server blocks; the linker still wants the symbol).
      send_client: () => {
        console.error(`[local-server] ${m.label}: send(...) called from client-side code in server runtime`);
      },
      send_server: (to: number, channel: number, dataPtr: number, reliable: number) => {
        // Copy out of wasm memory before handing off — delivery may re-enter
        // wasm (relocating the heap), which would invalidate an aliased slice.
        // `reliable` is honored per-transport: the loopback ignores it, a remote
        // peer routes the unreliable class to WebRTC's unordered channel.
        this.bufferOutbound(to, stampChannel(channel, readBytesPtr(m, dataPtr)), reliable !== 0);
      },
      register_channel: (channel: number) => {
        m.channels.add(channel & 0xffff);
      },

      // Parent uplink (docs/node-federation.md) — bind to the shared server
      // fabric (one link per LocalServer). connect_up opens the outbound socket
      // to the origin; send_up writes a channel-stamped envelope onto it.
      connect_up: (urlPtr: number, handoffPtr: number) => {
        const url = readStringPtr(m, urlPtr);
        // Copy the handoff out of wasm memory: the socket opens later, and the
        // heap may have moved by then.
        this.openParentLink(url, readBytesPtr(m, handoffPtr).slice());
      },
      send_up: (channel: number, dataPtr: number, reliable: number) => {
        this.sendUp(channel, readBytesPtr(m, dataPtr).slice(), reliable !== 0);
      },
      // A browser-local node has no spawn-tree parent; its origin URL is
      // threaded through the launch handoff (a later web-layer increment), so
      // The origin this node was rooted under, or "" when it is its own root.
      // Set by adoptParent (host_locally) / connect_up; lets a hosted script
      // gate an upward action on whether a parent exists (the editor hides its
      // cloud-publish button when this is empty).
      parent_url: () => allocString(m, this.parentUrl),
      // Unguessable, ASCII-safe id — a recorder keys each run by it, so every
      // run gets a fresh collision-free key (an empty-string stub would make
      // every run clobber the last). Mirrors the server's uuid() binding.
      uuid: () => allocString(m, makeUuid()),

      // Interest management — same shared geometry as the production server.
      set_interest: (slot: number, area: number, x: number, y: number, w: number, h: number) => {
        this.interest.set(slot, area, x, y, w, h);
      },
      relay_interested: (subject: number, channel: number, dataPtr: number, reliable: number) => {
        const data = stampChannel(channel, readBytesPtr(m, dataPtr));
        const isReliable = reliable !== 0;
        const { deliver, left } = this.interest.relay(subject, (r) => this.connectedSlots.has(r));
        for (const r of deliver) this.bufferOutbound(r, data, isReliable);
        if (this.peerLeftSink) for (const r of left) this.peerLeftSink(r, subject);
      },

      max_clients: () => this.maxClients,
      tick_rate: () => this.tickHz,
      set_tick_rate: (hz: number) => this.setTickRate(hz),
    };
  }

  // ----- parent uplink (docs/node-federation.md) -----

  // Open (or replace) the outbound link to the origin. Over it this node is an
  // ordinary client of `url`: the first message is the raw handoff, then every
  // message is a channel-stamped envelope — the exact client protocol, so the
  // origin handles a browser-local node with no special case.
  private openParentLink(url: string, handoff: Uint8Array): void {
    if (this.parentLink) { try { this.parentLink.close(); } catch { /* closing */ } }
    this.parentLinkReady = false;
    this.parentLinkQueue.length = 0;
    this.parentUrl = url;
    const s = this.uplinkFactory(url);
    s.binaryType = "arraybuffer";
    this.parentLink = s;

    s.addEventListener("open", () => {
      if (this.stopped || this.parentLink !== s) { try { s.close(); } catch { /* ignore */ } return; }
      s.send(handoff);
      this.parentLinkReady = true;
      for (const d of this.parentLinkQueue) s.send(d);
      this.parentLinkQueue.length = 0;
    });

    s.addEventListener("message", (ev) => {
      if (this.stopped || this.parentLink !== s) return;
      const d = ev.data;
      // Control frames (redirect / peer_left) arrive as TEXT; a parent-driven
      // redirect of a local node is future work, so the uplink ignores them.
      if (typeof d === "string") return;
      const buf = d instanceof ArrayBuffer ? new Uint8Array(d)
        : d instanceof Uint8Array ? d : new Uint8Array(0);
      for (const f of decodeEnvelope(buf)) {
        if (readChannel(f) < 0) continue;
        const body = f.subarray(2);
        for (const m of this.members) {
          this.callEvent(m, "event_server_receive_from_above", encodeBytes(m, body));
        }
      }
      this.flushEgress();
    });

    s.addEventListener("close", () => {
      if (this.parentLink === s) { this.parentLink = null; this.parentLinkReady = false; }
    });
    s.addEventListener("error", () => {
      console.error(`[local-server] parent link error (${url})`);
    });
  }

  // Write one frame up to the origin (queued until the link opens).
  private sendUp(channel: number, data: Uint8Array, reliable: boolean): void {
    void reliable; // a WebSocket uplink is reliable; the flag is honored per-transport
    const frame = stampChannel(channel, data);
    for (const datagram of encodeEnvelope([frame])) {
      const s = this.parentLink;
      // 1 === WebSocket.OPEN (BusSocket mirrors the WebSocket readyState codes).
      if (s && this.parentLinkReady && s.readyState === 1) s.send(datagram);
      else this.parentLinkQueue.push(datagram);
    }
  }

  // ----- tick scheduling -----

  private setTickRate(hz: number): void {
    if (this.stopped) return;
    if (hz <= 0 || hz > 1000) {
      console.error(`[local-server] ignoring set_tick_rate(${hz}): out of range`);
      return;
    }
    this.tickHz = hz;
    if (this.tickTimer !== null) {
      clearInterval(this.tickTimer);
      this.startTickTimer();
    }
  }

  private startTickTimer(): void {
    const intervalMs = Math.max(1, Math.round(1000 / this.tickHz));
    this.tickTimer = setInterval(() => this.fireTick(), intervalMs);
  }

  private fireTick(): void {
    // tickNum is per-instance (not per-member) and advances only on a clean
    // pass — a throwing member sees the same number again next tick.
    let ok = true;
    for (const m of this.members) {
      if (!this.callEvent(m, "event_server_tick", this.tickNum)) ok = false;
    }
    if (ok) this.tickNum++;
    // Every member has relayed for this tick; ship each recipient's buffered
    // frames as coalesced envelopes (no-op unless coalescing). End-of-tick
    // flush = zero added latency, and it's where cross-subject coalescing
    // (A, B, C → the same peer) happens.
    this.flushEgress();
  }
}
